'use client'

import { getChainConfigEntry } from '@/config/chain'
import { UserPosition } from '@/domain/market-info/marketInfo'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { DataTable } from '@/ui/molecules/data-table/DataTable'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { cn } from '@/ui/utils/style'
import { useCallback, useMemo, useState } from 'react'
import { useChainId } from 'wagmi'
import { ProtocolId } from '../constants'

interface CollateralAssetsSectionProps {
  assets: UserPosition[]
  totalCollateral: number
  mode?: 'borrow' | 'spend'
  showRadioSelection?: boolean
  selectedAsset?: UserPosition | null
  onAssetSelection?: (asset: UserPosition | null) => void
  protocol?: ProtocolId
}

export function CollateralAssetsSection({
  assets,
  totalCollateral,
  mode = 'borrow',
  showRadioSelection = false,
  selectedAsset: externalSelectedAsset,
  onAssetSelection,
  protocol = 'hypurrFi',
}: CollateralAssetsSectionProps) {
  const chainId = useChainId()
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message
  const { pooledPartnerPoints } = getChainConfigEntry(chainId)

  // Selection state for spend mode - initialize with external selection if provided
  // Only use externalSelectedAsset if it has the correct structure (UserPosition with reserve.token)
  const [internalSelectedAsset, setInternalSelectedAsset] = useState<string | null>(
    externalSelectedAsset && 'reserve' in externalSelectedAsset && externalSelectedAsset.reserve?.token?.symbol
      ? externalSelectedAsset.reserve.token.symbol
      : null,
  )

  // Use external selection if provided and valid, otherwise fall back to internal state
  const selectedAssetSymbol =
    externalSelectedAsset && 'reserve' in externalSelectedAsset && externalSelectedAsset.reserve?.token?.symbol
      ? externalSelectedAsset.reserve.token.symbol
      : internalSelectedAsset

  const handleAssetSelection = useCallback(
    (assetSymbol: string) => {
      setInternalSelectedAsset(assetSymbol)

      // Pass the selected asset to parent
      const selectedAssetData = assets.find((position) => position.reserve.token.symbol === assetSymbol)
      onAssetSelection?.(selectedAssetData || null)
    },
    [assets, onAssetSelection],
  )

  const assetsColumnDefinition = useMemo<{ [key: string]: ColumnDefinition<UserPosition> }>(
    () => ({
      asset: {
        header: 'Assets',
        headerAlign: 'left',
        showOnMobile: true,
        sortable: false,
        renderCell: (position) => (
          <div className="flex items-start gap-4">
            <TokenWithLogo
              token={position.reserve.token}
              reserveStatus={position.reserve.status}
              className="gap-3 text-left"
            />
          </div>
        ),
      },
      balance: {
        header: 'Balance',
        headerAlign: 'left',
        showOnMobile: true,
        sortable: true,
        sortingFn: (a, b) =>
          a.original.collateralBalance.multipliedBy(a.original.reserve.priceInUSD).toNumber() -
          b.original.collateralBalance.multipliedBy(b.original.reserve.priceInUSD).toNumber(),
        renderCell: (position) => (
          <div>
            <div className="font-medium text-foreground">{position.collateralBalance.toFixed(3)}</div>
            <div className="text-muted-foreground text-sm">
              ${position.collateralBalance.multipliedBy(position.reserve.priceInUSD).toFixed(2)}
            </div>
          </div>
        ),
      },
      apy: {
        header: <ApyTooltip variant="supply">Deposit APY</ApyTooltip>,
        headerAlign: 'right',
        showOnMobile: true,
        sortable: true,
        sortingFn: (a, b) =>
          (a.original.reserve.supplyAPY?.toNumber() || 0) - (b.original.reserve.supplyAPY?.toNumber() || 0),
        renderCell: (position) => {
          // For Euler, skip points logic and just display APY
          if (protocol === 'euler') {
            return (
              <ApyWithRewardsCell
                action="deposit"
                asset={position.reserve.token.symbol as any}
                apy={position.reserve.supplyAPY}
                points={undefined}
                partnerPoints={undefined}
                pointsError={false}
                pointsLoading={false}
              />
            )
          }

          // For HypurrFi pooled, use points logic
          const points = getByAddress({
            type: 'pooled',
            action: 'deposit',
            address: position.reserve.token.address,
          })

          return (
            <ApyWithRewardsCell
              action="deposit"
              asset={position.reserve.token.symbol as any}
              apy={position.reserve.supplyAPY}
              points={points}
              partnerPoints={pooledPartnerPoints[CheckedAddress(position.reserve.token.address)]?.points}
              pointsError={hasPointsError}
              pointsLoading={isPointsDataLoading}
            />
          )
        },
      },
    }),
    [getByAddress, hasPointsError, isPointsDataLoading, pooledPartnerPoints, protocol],
  )

  if (showRadioSelection) {
    return (
      <div className="flex flex-col gap-2">
        {assets.length === 0 ? (
          <Typography variant="p" className="text-muted-foreground text-sm">
            No collateral assets found
          </Typography>
        ) : (
          assets.map((position) => {
            const symbol = position.reserve.token.symbol
            const isSelected = selectedAssetSymbol === symbol
            const balance = position.collateralBalance.toFixed(3)
            const usdValue = position.collateralBalance.multipliedBy(position.reserve.priceInUSD).toFixed(2)
            const apyNum = position.reserve.supplyAPY ? Number(position.reserve.supplyAPY) * 100 : null

            return (
              <button
                key={symbol}
                type="button"
                onClick={() => handleAssetSelection(symbol)}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors',
                  isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary',
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <TokenIcon token={position.reserve.token} className="h-7 w-7 shrink-0" />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-sm">{symbol}</div>
                    <div className="truncate text-muted-foreground text-xs">{position.reserve.token.name}</div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-4 text-right">
                  <div>
                    <div className="font-medium text-sm">{balance}</div>
                    <div className="text-muted-foreground text-xs">${usdValue}</div>
                  </div>
                  {apyNum !== null && apyNum > 0 && (
                    <div className="w-14 font-medium text-green-400 text-sm">{apyNum.toFixed(2)}%</div>
                  )}
                </div>
              </button>
            )
          })
        )}
      </div>
    )
  }

  return (
    <div>
      {mode === 'borrow' && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex gap-4">
            <Typography className="font-semibold">Total deposited</Typography>
            <Typography className="font-semibold">${totalCollateral.toFixed(2)}</Typography>
          </div>
        </div>
      )}

      <DataTable
        columnDef={assetsColumnDefinition}
        data={assets}
        withPanel={false}
        gridTemplateColumnsClassName="grid-cols-3"
        emptyStateText="No collateral assets found"
      />
    </div>
  )
}
